// Temporary Hub-only diagnostics. Counts browser resource observations, not billing SKUs.
const STORAGE_KEY = "google-autocomplete-audit-v1";
const COUNTERS = [
  "autocomplete_requests", "details_requests", "other_places_requests",
  "selections", "clears", "input_changes", "save_attempts", "page_loads",
  "observer_errors",
];

export function classifyGoogleResource(name) {
  try {
    const url = new URL(name);
    if (!["maps.googleapis.com", "places.googleapis.com"].includes(url.hostname)) return null;
    const path = url.pathname;
    if (/\/AutocompletionService\.GetPredictions(?:Json)?$/.test(path)
      || /\/google\.maps\.places\.v1\.Places\/AutocompletePlaces$/.test(path)
      || /^\/maps\/api\/place\/autocomplete\/(json|xml)$/.test(path)) return "autocomplete_requests";
    if (/\/(?:PlaceService|PlacesService)\.GetPlaceDetails$/.test(path)
      || /\/google\.maps\.places\.v1\.Places\/GetPlace$/.test(path)
      || /^\/maps\/api\/place\/details\/(json|xml)$/.test(path)) return "details_requests";
    if (path.startsWith("/maps/api/place/") || path.includes("/google.maps.places.v1.Places/")) {
      return "other_places_requests";
    }
  } catch (_) { /* A malformed/unrelated resource is not address activity. */ }
  return null;
}

export class GoogleAutocompleteAudit {
  constructor(browser = typeof window === "undefined" ? null : window) {
    this.browser = browser;
    this.session = null;
    this.observer = null;
    this.seen = new Set();
    this.onPageHide = () => this.flush();
  }

  start(order, contour) {
    try {
      const config = order && order.google_autocomplete_audit;
      if (!this.browser || contour !== "legacy" || !config || config.version !== 1
        || !Number.isFinite(config.expires_at) || config.expires_at <= Date.now()) {
        this.stop();
        return;
      }
      const orderKey = `${order.is_created ? "create" : "save"}:${order.id || 0}:${order.country_id}`;
      if (this.session && this.session.order_key === orderKey) return;
      this.disconnect();
      let stored = null;
      try { stored = JSON.parse(this.browser.sessionStorage.getItem(STORAGE_KEY)); } catch (_) { /* Optional storage. */ }
      const resume = stored && stored.order_key === orderKey && stored.expires_at > Date.now()
        && typeof stored.session_id === "string" && /^[a-z0-9-]{10,64}$/.test(stored.session_id)
        && Number.isFinite(stored.started_at) && stored.started_at <= Date.now();
      this.session = {
        order_key: orderKey,
        session_id: resume ? stored.session_id : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`,
        started_at: resume ? stored.started_at : Date.now(),
        expires_at: config.expires_at,
        observer_supported: 0,
      };
      COUNTERS.forEach(key => {
        this.session[key] = resume && Number.isInteger(stored[key]) && stored[key] >= 0
          ? Math.min(stored[key], 1000000) : 0;
      });
      this.session.page_loads += 1;
      this.startTime = this.browser.performance ? this.browser.performance.now() : 0;
      this.seen = new Set();
      if (this.browser.PerformanceObserver) {
        try {
          this.observer = new this.browser.PerformanceObserver(list => {
            try {
              this.observeEntries(list.getEntries());
              this.persist();
            } catch (_) { this.increment("observer_errors"); }
          });
          this.observer.observe({ entryTypes: ["resource"] });
          this.session.observer_supported = 1;
        } catch (_) { this.session.observer_errors += 1; }
      }
      this.browser.addEventListener("pagehide", this.onPageHide);
      this.persist();
    } catch (_) { this.stop(); } // Diagnostics must not interfere with opening an order.
  }

  observeEntries(entries) {
    if (!this.session || this.session.expires_at <= Date.now()) return;
    entries.forEach(entry => {
      // A response finishing after switching orders still belongs to its original start time.
      if (entry.startTime < this.startTime) return;
      const kind = classifyGoogleResource(entry.name);
      if (!kind) return;
      const key = `${entry.startTime}:${entry.name}`;
      if (this.seen.has(key)) return;
      this.seen.add(key); // URLs stay only in memory; never persist or submit them.
      this.session[kind] = Math.min(this.session[kind] + 1, 1000000);
    });
  }

  increment(key) {
    if (!this.session || this.session.expires_at <= Date.now() || !COUNTERS.includes(key)) return;
    this.session[key] = Math.min(this.session[key] + 1, 1000000);
    this.persist();
  }

  flush() {
    if (!this.session) return;
    try {
      if (this.observer && this.observer.takeRecords) this.observeEntries(this.observer.takeRecords());
      if (this.browser.performance && this.browser.performance.getEntriesByType) {
        this.observeEntries(this.browser.performance.getEntriesByType("resource"));
      }
    } catch (_) { this.session.observer_errors += 1; }
    this.persist();
  }

  snapshot(order, contour) {
    try {
      if (contour !== "legacy" || !this.session || this.session.expires_at <= Date.now()) return null;
      const orderKey = `${order.is_created ? "create" : "save"}:${order.id || 0}:${order.country_id}`;
      if (this.session.order_key !== orderKey) return null;
      this.flush();
      this.increment("save_attempts");
      const report = {
        version: 1,
        session_id: this.session.session_id,
        started_at: this.session.started_at,
        elapsed_ms: Math.min(Math.max(0, Date.now() - this.session.started_at), 2592000000),
        observer_supported: this.session.observer_supported,
      };
      COUNTERS.forEach(key => { report[key] = this.session[key]; });
      return report;
    } catch (_) { return null; } // Saving an order never depends on telemetry.
  }

  complete(report) {
    if (report && this.session && report.session_id === this.session.session_id) this.stop();
  }

  persist() {
    try {
      if (this.session) this.browser.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.session));
    } catch (_) { /* Storage can be disabled or full. In-memory counters still work. */ }
  }

  disconnect() {
    try { if (this.observer) this.observer.disconnect(); } catch (_) { /* Best effort. */ }
    try { if (this.browser) this.browser.removeEventListener("pagehide", this.onPageHide); } catch (_) { /* Best effort. */ }
    this.observer = null;
  }

  stop() {
    this.disconnect();
    this.session = null;
    this.seen.clear();
    try { if (this.browser) this.browser.sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* Optional storage. */ }
  }
}

export default new GoogleAutocompleteAudit();
